'use client'
import { useState } from 'react'
import dynamic from 'next/dynamic'
import type { GuardianInfo, WaiverSignPayload } from '../../bookings/[slug]/WaiverCanvas'

// Loaded on demand. MarkdownRenderer pulls react-markdown plus the whole
// unified/micromark/parse5 pipeline (~318 KB raw, ~102 KB brotli). Imported
// statically from a client component it lands in the SHARED client graph, so
// every storefront page — homepage, blog, PDP — downloaded it even though only
// the booking flow renders markdown in the browser. `dynamic()` here is a real
// async chunk because this is a genuine client→client edge with no barrel in
// between; the same call inside a Server Component, or through the shared-ui
// barrel, is a measured no-op.
// `loading` is not cosmetic here. The canvas mounts on client state
// (`view.showCanvas`), i.e. AFTER hydration, so these terms are not in the
// SSR'd HTML and there IS a window where the chunk has not arrived. A signature
// pad next to silently-blank legal terms is the one failure mode worth spending
// a placeholder on.
const MarkdownRenderer = dynamic(
  () => import('@/components/MarkdownRenderer').then((m) => m.MarkdownRenderer),
  { loading: () => <p className="text-sm text-[var(--color-text-secondary)]">Loading terms…</p> }
)
import { initialActiveSlot, waiverSlotView, type WaiverMode } from '../waiverSlotModel'
import { WAIVER_REASON_COPY } from '../copy'
import type { SignFn } from '../types'

// Browser-only signature canvas — code-split so the PDP bundle skips it until
// the visitor reaches the confirmed phase.
const WaiverCanvas = dynamic(() => import('../../bookings/[slug]/WaiverCanvas').then(m => m.WaiverCanvas), {
  ssr: false,
  loading: () => (
    <div className="py-8 text-center text-sm text-[var(--color-text-secondary,#5b665f)]">Loading waiver…</div>
  ),
})

type Props = {
  mode: WaiverMode
  /** Live N — derived from the booking, never from the flow definition. */
  requiredRiders: number
  initialSignedSlots: number[]
  /** Null when the dealer has published no waiver page. */
  waiverTerms: string | null
  minRiderAge: number
  allowMinors: boolean
  sign: SignFn
  onAllSigned?: () => void
}

/**
 * The per-rider (N-of-M) waiver shell. Owns slot state; knows nothing about
 * which capability authorised the signature. Completion keys on the stable slot
 * number and seeds from the booking, so a closed-and-reopened durable page
 * resumes exactly where it left off.
 */
export function WaiverStep({
  mode, requiredRiders, initialSignedSlots, waiverTerms, minRiderAge, allowMinors, sign, onAllSigned,
}: Props) {
  const [signedSlots, setSignedSlots] = useState<number[]>(initialSignedSlots)
  const [activeSlot, setActiveSlot] = useState<number | null>(() => initialActiveSlot(mode, requiredRiders))
  const [signing, setSigning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A guardian's identity carries to the next minor on the same booking (the
  // parent-of-several journey). The attestation checkbox never carries.
  const [carryGuardian, setCarryGuardian] = useState<GuardianInfo | null>(null)

  const view = waiverSlotView({ mode, requiredRiders, signedSlots, activeSlot })

  // Preserve each surface's voice — a session meets, a daily rental is picked
  // up. Flattening these to one string was a real regression risk in the
  // de-triplication; the copy differed between the two forms.
  const plural = requiredRiders === 1 ? '' : 's'
  const laterPlace = mode === 'session' ? 'at your session' : 'at pickup'
  const seeYou = mode === 'session' ? 'see you out there' : 'see you at pickup'

  // No published waiver → inform, never dead-end. Content steps above still
  // render, which is what makes the generalized email send-gate safe.
  if (!waiverTerms) {
    return (
      <p className="rounded-md border border-[var(--color-border,#e2e6e1)] p-4 text-sm text-[var(--color-text-secondary,#5b665f)]">
        Your dealer is finishing setup — you&apos;ll sign your waiver{plural} {laterPlace}.
      </p>
    )
  }

  if (view.allSigned) {
    return (
      <p className="rounded-md border border-[var(--color-border,#e2e6e1)] p-4 text-sm text-[var(--color-text-secondary,#5b665f)]">
        {requiredRiders > 1 ? 'All waivers signed' : 'Waiver signed'} — {seeYou}.
      </p>
    )
  }

  const markSigned = (slot: number) =>
    setSignedSlots(prev => (prev.includes(slot) ? prev : [...prev, slot]))

  async function handleSign(payload: WaiverSignPayload) {
    // Capture before the await — `view` is recomputed on every render.
    const slotToRecord = view.slotToRecord
    const slotForSign = view.slotForSign

    setSigning(true)
    setError(null)
    const r = await sign(payload, slotForSign)
    setSigning(false)

    if (r.ok) {
      if (payload.guardian) setCarryGuardian({ ...payload.guardian, attestation: false })
      markSigned(slotToRecord)
      setActiveSlot(null)
      if (r.final || signedSlots.length + 1 >= requiredRiders) onAllSigned?.()
      return
    }

    // Another device filled the whole set while this one was open.
    if (r.reason === 'all_slots_filled') {
      setSignedSlots(Array.from({ length: requiredRiders }, (_, i) => i + 1))
      onAllSigned?.()
      return
    }

    // Another device took this slot — reflect it and let them pick again.
    if (r.reason === 'slot_taken' || r.reason === 'already_signed') {
      markSigned(slotToRecord)
      setActiveSlot(null)
    }

    setError(WAIVER_REASON_COPY[r.reason] ?? "That didn't go through. Please check the details and try again.")
  }

  return (
    <div className="space-y-4">
      {requiredRiders > 1 && (
        <p className="text-sm font-medium text-[var(--color-text-secondary,#5b665f)]">
          {signedSlots.length} of {requiredRiders} waivers signed
          {view.showPicker ? ' — who’s signing now?' : ''}
        </p>
      )}

      {/* Sessions bind each signature to a chosen rider — pick before signing. */}
      {view.showPicker && (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: requiredRiders }, (_, i) => i + 1).map(slot => {
            const done = signedSlots.includes(slot)
            const isSel = activeSlot === slot
            return (
              <button
                key={slot}
                type="button"
                disabled={done}
                onClick={() => { setActiveSlot(isSel ? null : slot); setError(null) }}
                aria-pressed={isSel}
                className={[
                  'min-h-[44px] rounded-md border px-3 text-sm transition-colors',
                  done
                    ? 'border-[var(--color-border,#e2e6e1)] text-[var(--color-text-muted,#a8b0aa)] line-through cursor-not-allowed'
                    : isSel
                      ? 'border-[var(--color-cta,#2f5d43)] bg-[var(--color-cta,#2f5d43)] text-white'
                      : 'border-[var(--color-border,#e2e6e1)] text-[var(--color-text-primary,#1f2723)] hover:bg-[var(--color-border,#e2e6e1)]/40',
                ].join(' ')}
              >
                Rider {slot}
              </button>
            )
          })}
        </div>
      )}

      {view.showCanvas && (
        <WaiverCanvas
          // Remount per rider: fresh fields + a cleared signature pad.
          key={`${activeSlot ?? 0}:${signedSlots.length}`}
          onSign={handleSign}
          signing={signing}
          termsNode={<MarkdownRenderer markdown={waiverTerms} />}
          minRiderAge={minRiderAge}
          allowMinors={allowMinors}
          carryGuardian={carryGuardian}
        />
      )}

      {error && (
        <p className="rounded-md border border-[#f0d9cf] bg-[#fbf0ea] p-3 text-sm text-[#9a4b32]">{error}</p>
      )}
    </div>
  )
}
